import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import './AddUser.css';
import { getClient } from '../utils/apiClient.js';
import { useTranslation } from 'react-i18next';

function AddUser() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: '',
    userName: '',
    bio: '',
    offer: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);

  React.useEffect(() => {
    checkAuth();
  }, []);

  React.useEffect(() => {
    if (form.userName && form.userName.length >= 3) {
      const timeoutId = setTimeout(() => {
        checkUsernameAvailability(form.userName);
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUsernameAvailable(null);
    }
  }, [form.userName]);

  async function checkAuth() {
    try {
      await getCurrentUser();
    } catch (error) {
      navigate('/login');
    }
  }

  async function checkUsernameAvailability(userName) {
    setCheckingUsername(true);
    const client = getClient('userPool');

    try {
      const { data } = await client.models.UserProfile.list({
        filter: { userName: { eq: userName } }
      });

      setUsernameAvailable(data && data.length === 0);
    } catch (error) {
      console.error('Error verificando userName:', error);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.userName || form.userName.trim().length < 3) {
      setMessage(t('admin.validation.usernameMinLength'));
      return;
    }

    if (usernameAvailable === false) {
      setMessage(t('admin.validation.usernameTaken'));
      return;
    }

    setSaving(true);
    setMessage('');
    const client = getClient('userPool');

    try {
      const currentUser = await getCurrentUser();
      const randomId = `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      await client.models.UserProfile.create({
        userId: randomId,
        name: form.name,
        userName: form.userName,
        bio: form.bio,
        offer: form.offer
      });

      setMessage(t('admin.success'));
      setForm({ name: '', userName: '', bio: '', offer: '' });
      setUsernameAvailable(null);
    } catch (error) {
      console.error('Error agregando usuario:', error);
      setMessage(t('admin.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="add-user-container">
      <div className="add-user-content">
        <h2>{t('admin.title')}</h2>
        <p>{t('admin.subtitle')}</p>

        <form onSubmit={handleSubmit} className="add-user-form">
          <div className="form-group">
            <label htmlFor="name">{t('admin.form.nameLabel')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t('admin.form.namePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="userName">{t('admin.form.usernameLabel')}</label>
            <div className="username-input-wrapper">
              <input
                type="text"
                id="userName"
                name="userName"
                value={form.userName}
                onChange={handleChange}
                placeholder={t('admin.form.usernamePlaceholder')}
                required
                pattern="^[a-zA-Z0-9_]{3,20}$"
                title="Solo letras, números y guión bajo. Entre 3 y 20 caracteres."
                className={
                  form.userName.length >= 3 
                    ? usernameAvailable === true 
                      ? 'input-success' 
                      : usernameAvailable === false 
                      ? 'input-error' 
                      : ''
                    : ''
                }
              />
              {checkingUsername && (
                <span className="username-status checking">{t('admin.checking')}</span>
              )}
              {!checkingUsername && usernameAvailable === true && (
                <span className="username-status available">{t('admin.available')}</span>
              )}
              {!checkingUsername && usernameAvailable === false && (
                <span className="username-status taken">{t('admin.taken')}</span>
              )}
            </div>
            <small className="username-hint">
              {t('admin.usernameHint')}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="bio">{t('admin.form.bioLabel')}</label>
            <textarea
              id="bio"
              name="bio"
              value={form.bio}
              onChange={handleChange}
              placeholder={t('admin.form.bioPlaceholder')}
              rows="4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="offer">{t('admin.form.offerLabel')}</label>
            <textarea
              id="offer"
              name="offer"
              value={form.offer}
              onChange={handleChange}
              placeholder={t('admin.form.offerPlaceholder')}
              rows="3"
            />
          </div>

          <button 
            type="submit" 
            className="btn-save" 
            disabled={saving || usernameAvailable === false}
          >
            {saving ? t('admin.button.adding') : t('admin.button.addUser')}
          </button>

          {message && (
            <div className={`message ${message.includes('❌') ? 'error' : 'success'}`}>
              {message}
            </div>
          )}
        </form>

        <div className="quick-actions">
          <button onClick={() => navigate('/users')} className="btn-view-users">
            {t('admin.quickActions.viewUsers')}
          </button>
          <button onClick={() => navigate('/')} className="btn-back">
            {t('admin.quickActions.backHome')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddUser;